import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { gradientText, gradientButton } from '../theme';
import { supabase } from '../lib/supabase';

function Signup() {
  const { t } = useTranslation();
  const { signUp } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    terms?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);

  // Capturer le code de parrainage dès le chargement
  useEffect(() => {
    // Extraire les paramètres après le # et ?
    const hashPart = window.location.hash;
    const queryIndex = hashPart.indexOf('?');
    
    if (queryIndex !== -1) {
      const queryPart = hashPart.substring(queryIndex + 1);
      const params = new URLSearchParams(queryPart);
      const referralCode = params.get('ref');
      
      if (referralCode) {
        console.log("[Signup] Found referral code in URL on load:", referralCode);
        localStorage.setItem('referral_code', referralCode);
      }
    }
  }, []);

  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      terms?: string;
    } = {};

    if (!email) {
      newErrors.email = t('auth.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('auth.validation.emailInvalid');
    }

    if (!password) {
      newErrors.password = t('auth.validation.passwordRequired');
    } else if (password.length < 8) {
      newErrors.password = t('auth.validation.passwordTooShort');
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.validation.passwordMismatch');
    }

    if (!acceptTerms) {
      newErrors.terms = t('auth.validation.termsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isLoading) return;

    setIsLoading(true);
    setAuthError(null);

    // Récupérer l'ID de la relation en attente et l'URL de redirection pour le logging
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    const redirectPath = localStorage.getItem('redirectAfterAuth');
    
    // Vérifier s'il y a un code de parrainage dans l'URL
    // Extraire les paramètres après le # et ?
    const hashPart = window.location.hash;
    const queryIndex = hashPart.indexOf('?');
    let referralCode = null;
    
    if (queryIndex !== -1) {
      const queryPart = hashPart.substring(queryIndex + 1);
      const params = new URLSearchParams(queryPart);
      referralCode = params.get('ref');
    }
    
    // Stocker le code de parrainage dans localStorage s'il existe
    if (referralCode) {
      localStorage.setItem('referral_code', referralCode);
      console.log("[Signup] Referral code stored in localStorage:", referralCode);
    }
    
    console.log("[Signup] Starting signup process:", {
      email,
      pendingRelationId,
      redirectPath,
      referralCode
    });

    const { error, emailVerificationSent } = await signUp(email, password);
    
    if (error) {
      if (error.message === 'auth.signup.existingGoogleAccount') {
        setAuthError(t('auth.signup.existingGoogleAccount'));
      } else {
        setAuthError(t('auth.signup.signupError'));
      }
      setIsLoading(false);
      return;
    }

    // Si aucun pendingRelationId n'existe, marquer comme nouvel utilisateur
    // pour la redirection vers SignUpFlow
    if (!pendingRelationId) {
      localStorage.setItem('isNewUser', 'true');
      console.log("[Signup] Marked as new user, will redirect to signup flow after verification");
    }

    if (emailVerificationSent) {
      console.log("[Signup] Email verification sent, showing verification screen");
      setEmailVerificationSent(true);
    } else {
      console.log("[Signup] Signup successful, redirection will be handled automatically");
    }
    
    setIsLoading(false);
  };

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    setAuthError(null);

    // Récupérer le code de parrainage
    // Extraire les paramètres après le # et ?
    const hashPart = window.location.hash;
    const queryIndex = hashPart.indexOf('?');
    let referralCode = null;
    
    if (queryIndex !== -1) {
      const queryPart = hashPart.substring(queryIndex + 1);
      const params = new URLSearchParams(queryPart);
      referralCode = params.get('ref');
    }
    
    // Stocker le code dans localStorage pour le récupérer après redirection OAuth
    if (referralCode) {
      localStorage.setItem('referral_code', referralCode);
      console.log("[Signup] Referral code stored in localStorage:", referralCode);
    }

    // Vérifier si un pendingRelationId existe déjà
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    console.log("[Signup] Starting Google signup with pending relation:", pendingRelationId);

    // Si aucun pendingRelationId n'existe, marquer comme nouvel utilisateur
    // pour la redirection vers SignUpFlow après l'authentification Google
    if (!pendingRelationId) {
      localStorage.setItem('isNewUser', 'true');
      // Ajouter une persistance pour s'assurer que le flag reste disponible après redirection OAuth
      sessionStorage.setItem('isNewUser_persisted', 'true');
      
      // Amélioration: Définir un flag pour l'authentification Google spécifiquement
      sessionStorage.setItem('google_signup', 'true');
      
      console.log("[Signup] Google signup: Marked as new user, will redirect to signup flow after auth");
    }

    // Stocker le chemin actuel pour la redirection si ce n'est pas déjà fait
    if (!localStorage.getItem('redirectAfterAuth')) {
      const currentPath = window.location.hash.substring(1);
      if (currentPath && currentPath !== '/login' && currentPath !== '/signup') {
        localStorage.setItem('redirectAfterAuth', currentPath);
        console.log("[Signup] Stored current path for redirect:", currentPath);
      }
    }

    // S'assurer que nous n'avons pas déjà une redirection en cours
    localStorage.removeItem('redirectionInProgress');

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          },
          scopes: 'email profile'
        }
      });
      
      if (error) throw error;
      
      console.log("[Signup] Google auth initiated, redirecting to Google...");
    } catch (error) {
      console.error('[Signup] Google login error:', error);
      setAuthError(t('auth.signup.googleError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Si l'email de vérification a été envoyé, afficher le message
  if (emailVerificationSent) {
    return (
      <div className="min-h-screen bg-black flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            src="/Final Logo 192.png"
            alt="Logo"
            className="mx-auto w-16 h-16 mb-4"
          />
          <h2 className={`text-center text-2xl font-bold ${gradientText} mb-6`}>
            {t('auth.signup.emailVerification.title')}
          </h2>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <div className="bg-[#0A0A0A] py-6 px-4 shadow sm:rounded-2xl sm:px-8">
            <div className="text-center">
              <p className="text-gray-400 mb-6">
                {t('auth.signup.emailVerification.message', { email })}
              </p>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`w-full ${gradientButton} text-white font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 text-sm`}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('auth.signup.emailVerification.resend')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          src="/Final Logo 192.png"
          alt="Logo"
          className="mx-auto w-16 h-16 mb-4"
        />
        <h2 className={`text-center text-2xl font-bold ${gradientText} mb-6`}>
          {t('auth.signup.title')}
        </h2>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="bg-[#0A0A0A] py-6 px-4 shadow sm:rounded-2xl sm:px-8">
          {authError && (
            <div className="mb-4 p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
              {authError}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full bg-black border ${
                    errors.email ? 'border-red-500' : 'border-gray-800'
                  } rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm`}
                  placeholder={t('auth.signup.email')}
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full bg-black border ${
                    errors.password ? 'border-red-500' : 'border-gray-800'
                  } rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm`}
                  placeholder={t('auth.signup.password')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full bg-black border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-800'
                  } rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm`}
                  placeholder={t('auth.signup.confirmPassword')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
              )}
            </div>

            <div>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 h-3.5 w-3.5 rounded border-gray-800 text-primary-end focus:ring-primary-end focus:ring-offset-black"
                  disabled={isLoading}
                />
                <span className="text-xs text-gray-400">
                  {t('auth.signup.terms')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      // Ouvrir dans un nouvel onglet
                      window.open('/#/privacy-policy', '_blank');
                    }}
                    className={`${gradientText} hover:opacity-80 transition-opacity`}
                  >
                    {t('auth.signup.privacyPolicy')}
                  </button>
                </span>
              </label>
              {errors.terms && (
                <p className="mt-1 text-xs text-red-500">{errors.terms}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${gradientButton} text-white font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 text-sm`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {t('auth.signup.submit')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-[#0A0A0A] text-gray-400">{t('common.or')}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={isLoading}
              className="mt-4 w-full bg-white hover:bg-gray-50 text-gray-900 font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('auth.signup.googleButton')}
            </button>
          </div>

          <p className="mt-5 text-center text-xs text-gray-400">
            {t('auth.signup.hasAccount')}{' '}
            <Link
              to="/login"
              className={`${gradientText} hover:opacity-80 transition-opacity`}
            >
              {t('auth.signup.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;